import { PrimaryLayout } from '@/layouts/primary';
import { useSearch } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo } from 'react';
import { BagScanContextProvider, CustomerScanSubRoute } from './context';
import { BagScanErrorHandler } from './error-handler';
import { useBagScanContext } from './hook';
import { ScanFooter } from './scan-footer';
import { BagScanMaterialConfirmPage } from './subroutes/material-confirm';
import { BagScanScanPage } from './subroutes/scan';
import { BagScanScanInstructionsPage } from './subroutes/scan-instructions';
import { BagScanResultPage } from './subroutes/scan-result';

export const CustomerBagScanPage = () => {
	const params: { code?: string } = useSearch({ from: '/_app/scan' });

	return (
		<BagScanContextProvider initialCode={params.code}>
			<BagScanPageRoot />
		</BagScanContextProvider>
	);
};

const BagScanPageRoot = () => {
	const { page } = useBagScanContext();

	const shouldHideFooter = useMemo(() => {
		return page === 'SCAN' || page === 'MATERIAL_CONFIRM' || page === 'RESULT';
	}, [page]);

	return (
		<PrimaryLayout context={shouldHideFooter ? undefined : 'home'}>
			<AnimatePresence>
				<SubRoute
					key="INSTRUCTIONS"
					id="INSTRUCTIONS"
					component={<BagScanScanInstructionsPage />}
				/>
				<SubRoute key="SCAN" id="SCAN" component={<BagScanScanPage />} />
				<SubRoute
					key="MATERIAL_CONFIRM"
					id="MATERIAL_CONFIRM"
					component={<BagScanMaterialConfirmPage />}
				/>
				<SubRoute key="RESULT" id="RESULT" component={<BagScanResultPage />} />
			</AnimatePresence>
			<BagScanErrorHandler />
			<ScanFooter />
		</PrimaryLayout>
	);
};

const SubRoute = ({ id, component }: { id: CustomerScanSubRoute; component: React.ReactNode }) => {
	const { page } = useBagScanContext();

	if (page !== id) return null;

	return (
		<motion.div
			key={id}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.3 }}
		>
			{component}
		</motion.div>
	);
};
